import { useId, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleTrigger } from '@/components/ui/collapsible';

type Props = {
  text: string;
  clampLines?: number;
  className?: string;
  expandLabel: string;
  collapseLabel: string;
};

export const ExpandableText: React.FC<Props> = ({ text, clampLines = 4, className, expandLabel, collapseLabel }) => {
  const [expanded, setExpanded] = useState(false);
  const [isClampable, setIsClampable] = useState(false);
  const [collapsedHeight, setCollapsedHeight] = useState<number>();
  const [fullHeight, setFullHeight] = useState<number>();
  const textRef = useRef<HTMLDivElement>(null);
  const textId = useId();

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const measure = () => {
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
      setCollapsedHeight(lineHeight * clampLines);
      setFullHeight(el.scrollHeight);
      setIsClampable(el.scrollHeight > lineHeight * clampLines + 1);
    };

    measure();

    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [text, clampLines]);

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded} className='flex flex-col gap-[4px]'>
      <div
        id={textId}
        ref={textRef}
        className={clsx(
          className,
          'whitespace-pre-wrap wrap-break-word overflow-hidden transition-[max-height] duration-300 ease-in-out',
        )}
        style={{ maxHeight: (expanded ? fullHeight : collapsedHeight) ?? 'none' }}
      >
        {text}
      </div>

      {(isClampable || expanded) && (
        <CollapsibleTrigger asChild aria-controls={textId}>
          <Button
            variant='link'
            className='self-start h-auto p-0 text-s font-medium text-primary-green-day hover:no-underline'
          >
            {expanded ? collapseLabel : expandLabel}
          </Button>
        </CollapsibleTrigger>
      )}
    </Collapsible>
  );
};
